/**
 * WebAppContainer
 * 
 * A container component specifically for web that provides:
 * - Centered layout with max-width constraint (430px)
 * - Proper containment for absolute/fixed positioned children
 * - Consistent app boundaries across all web screens
 * 
 * This component should wrap the main app content on web to ensure
 * all overlays, modals, and other positioned elements stay within
 * the app boundaries.
 */

import React, { createContext, useContext, useRef, useEffect, useState, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { WEB_APP_MAX_WIDTH, WEB_COLORS } from './constants';

// Context to share the app container measurements with child components
interface WebAppContextValue {
  containerWidth: number;
  containerLeft: number;
}

const WebAppContext = createContext<WebAppContextValue>({
  containerWidth: WEB_APP_MAX_WIDTH,
  containerLeft: 0,
});

export const useWebAppContainer = () => useContext(WebAppContext);

interface WebAppContainerProps {
  children: React.ReactNode;
}

const WebAppContainer: React.FC<WebAppContainerProps> = ({ children }) => {
  const [containerWidth, setContainerWidth] = useState(WEB_APP_MAX_WIDTH);
  const [containerLeft, setContainerLeft] = useState(0);

  // Calculate container position based on window size
  const updateMeasurements = useCallback(() => {
    if (typeof window !== 'undefined') {
      const windowWidth = window.innerWidth;
      const width = Math.min(windowWidth, WEB_APP_MAX_WIDTH);
      const left = Math.max(0, (windowWidth - WEB_APP_MAX_WIDTH) / 2);
      setContainerWidth(width);
      setContainerLeft(left);
    }
  }, []);

  // Update measurements on mount and resize
  useEffect(() => {
    updateMeasurements();
    window.addEventListener('resize', updateMeasurements);
    return () => window.removeEventListener('resize', updateMeasurements);
  }, [updateMeasurements]);

  return (
    <WebAppContext.Provider value={{ containerWidth, containerLeft }}>
      <View style={styles.container}>
        {children}
      </View>
    </WebAppContext.Provider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: WEB_APP_MAX_WIDTH,
    backgroundColor: WEB_COLORS.APP_BACKGROUND,
    overflow: 'hidden',
    position: 'relative',
  },
});

export default WebAppContainer;

